
import WebGLEntity from "./WebGLEntity";

class PolylineCollection extends WebGLEntity {
    constructor(gl, polyline, tex_image, line_width, offset_scale) {
        super();

        this.width = line_width;
        this.scale = offset_scale;

        const vertexSource = `
        uniform mat4 u_matrix;

        attribute vec2 a_pos;
        attribute float a_min_zoom;
        attribute vec2 a_extrusion;
        attribute vec2 a_offset;
        attribute vec2 a_tex_coord;
        uniform lowp float u_offset_scale;
        uniform lowp float u_map_aspect;
        uniform lowp float u_zoom;
        uniform lowp float u_line_width;

        varying highp vec2 v_tex_coord;

        mat2 scalingMatrix(float x_scale, float y_scale) {
            return mat2(x_scale, 0.0,
                        0.0,     y_scale);
        }

        void main() {
            vec4 center_pos = u_matrix * vec4(a_pos.xy, 0.0, 1.0);
            vec2 offset = vec2(0.0, 0.0);
            
            if (u_zoom >= a_min_zoom) {
                offset = a_offset + u_line_width * a_extrusion;

                mat2 map_stretch_matrix = scalingMatrix(u_offset_scale, u_offset_scale / u_map_aspect);
                offset = map_stretch_matrix * offset;
            }

            gl_Position = center_pos + vec4(offset, 0.0, 0.0);
            v_tex_coord = a_tex_coord;
        }`;

        const fragmentSource = `
        varying highp vec2 v_tex_coord;

        uniform sampler2D u_sampler;

        void main() {
            if (v_tex_coord.x > 1.0) {
                discard;
            }

            lowp vec4 tex_color = texture2D(u_sampler, v_tex_coord);
            gl_FragColor = tex_color;
        }`;

        this.program = this._compileAndLinkShaders(gl, vertexSource, fragmentSource);

        this.n_pts = polyline['npts'];
        this.a_origin = this._setupVertices(gl, this.program, polyline['origin'], 2, 'a_pos');
        this.a_offset = this._setupVertices(gl, this.program, polyline['verts'], 2, 'a_offset');
        this.a_extrusion = this._setupVertices(gl, this.program, polyline['extrusion'], 2, 'a_extrusion');
        this.a_min_zoom = this._setupVertices(gl, this.program, polyline['zoom'], 1, 'a_min_zoom');

        this.texture = this._setupTexture(gl, this.program, tex_image, 'u_sampler', 
            polyline['texcoords'], 'a_tex_coord');

        this.u_offset_scale = gl.getUniformLocation(this.program, 'u_offset_scale');
        this.u_line_width = gl.getUniformLocation(this.program, 'u_line_width');
        this.u_map_aspect = gl.getUniformLocation(this.program, 'u_map_aspect');
        this.u_zoom = gl.getUniformLocation(this.program, 'u_zoom');
    }

    render(gl, matrix, [map_width, map_height], map_zoom) {
        gl.useProgram(this.program);
        gl.uniformMatrix4fv(
            gl.getUniformLocation(this.program, 'u_matrix'),
            false,
            matrix
        );

        gl.enable(gl.BLEND);
        gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

        gl.uniform1f(this.u_offset_scale, this.scale * (map_height / map_width));
        gl.uniform1f(this.u_line_width, this.width);
        gl.uniform1f(this.u_map_aspect, map_height / map_width);
        gl.uniform1f(this.u_zoom, map_zoom);

        this._bindTexture(gl, 0, this.texture);
        this._bindVertices(gl, this.a_origin);
        this._bindVertices(gl, this.a_offset);
        this._bindVertices(gl, this.a_extrusion);
        this._bindVertices(gl, this.a_min_zoom);

        gl.drawArrays(gl.TRIANGLE_STRIP, 0, this.n_pts);
    }
}

export default PolylineCollection;